// src/hooks/useNetwork.ts
import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';

type ConnectionType = 'wifi' | 'cellular' | 'ethernet' | 'none' | 'unknown';

export const useNetwork = () => {
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const [connectionType, setConnectionType] = useState<ConnectionType>('unknown');
  const [isLoading, setIsLoading] = useState(true);

  const checkNetwork = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      let online: boolean;
      let type: ConnectionType;

      if (!Capacitor.isNativePlatform() && typeof window !== 'undefined' && window.navigator) {
        // Web environment
        online = navigator.onLine;
        const navConnection = navigator as Navigator & {
          connection?: { effectiveType?: string };
        };
        const effectiveType = navConnection.connection?.effectiveType;
        type = 
          effectiveType === 'wifi' ? 'wifi' :
          effectiveType === 'cellular' ? 'cellular' :
          effectiveType === 'ethernet' ? 'ethernet' : 'unknown';
      } else {
        // Native environment (Android/iOS)
        const status = await Network.getStatus();
        online = status.connected;
        type = status.connectionType as ConnectionType;
      }

      setIsOnline(online);
      setConnectionType(type);
      return online;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let listener: { remove: () => void } | undefined;

    const updateStatus = () => checkNetwork();

    const initializeListeners = async () => {
      if (!Capacitor.isNativePlatform() && typeof window !== 'undefined' && window.navigator) {
        // Web listeners
        window.addEventListener('online', updateStatus);
        window.addEventListener('offline', updateStatus);

        // Network Information API
        const navConnection = navigator as Navigator & {
          connection?: { 
            addEventListener?: (type: string, listener: () => void) => void;
          };
        };

        navConnection.connection?.addEventListener?.('change', updateStatus);
      } else {
        // Native listener (Android/iOS)
        const l = await Network.addListener('networkStatusChange', updateStatus);
        listener = l;
      }
    };

    initializeListeners();
    checkNetwork(); // Initial check

    return () => {
      if (!Capacitor.isNativePlatform()) {
        window.removeEventListener('online', updateStatus);
        window.removeEventListener('offline', updateStatus);
      } else if (listener) {
        listener.remove();
      }
    };
  }, [checkNetwork]);

  return { isOnline, connectionType, isLoading, checkNetwork };
};